import { useEffect, useRef } from "react";
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from "d3-force";

interface SimNode extends SimulationNodeDatum {
  r: number;
}

type SynapsePulse = {
  i: number;
  j: number;
  phase: number;
  speed: number;
};

type HeroForceCanvasProps = {
  scrollProgress: number;
  enabled: boolean;
};

const NODE_COUNT = 48;

function initNodes(width: number, height: number): SimNode[] {
  return Array.from({ length: NODE_COUNT }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: 0,
    vy: 0,
    r: 2.5 + Math.random() * 7,
  }));
}

const SYNAPSE_PULSE_COUNT = 12;

function initSynapsePulses(nodes: SimNode[]): SynapsePulse[] {
  const n = nodes.length;
  if (n < 2) return [];
  const pulses: SynapsePulse[] = [];
  const cap = Math.min(SYNAPSE_PULSE_COUNT, Math.max(8, Math.floor(n * 0.3)));

  for (let k = 0; k < cap; k++) {
    const i = Math.floor(Math.random() * n);
    let bestJ = -1;
    let bestD = Infinity;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const a = nodes[i];
      const b = nodes[j];
      if (a.x == null || a.y == null || b.x == null || b.y == null) continue;
      const d = Math.hypot(b.x - a.x, b.y - a.y);
      if (d < bestD && d < 130) {
        bestD = d;
        bestJ = j;
      }
    }
    if (bestJ < 0) {
      let j = Math.floor(Math.random() * n);
      while (j === i) j = Math.floor(Math.random() * n);
      bestJ = j;
    }
    pulses.push({
      i,
      j: bestJ,
      phase: Math.random(),
      speed: 0.0035 + Math.random() * 0.006,
    });
  }
  return pulses;
}

export function HeroForceCanvas({ scrollProgress, enabled }: HeroForceCanvasProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const nodesRef = useRef<SimNode[]>([]);
  const rafRef = useRef<number>(0);
  const retryRef = useRef<number>(0);
  const frameRef = useRef(0);
  const visibleRef = useRef(true);
  const scrollRef = useRef(scrollProgress);

  scrollRef.current = scrollProgress;

  useEffect(() => {
    if (!enabled) return;

    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w = 0;
    let h = 0;
    let sim: ReturnType<typeof forceSimulation<SimNode>> | null = null;
    let cancelled = false;
    let synapsePulses: SynapsePulse[] = [];
    let dragIndex: number | null = null;
    let activePointerId: number | null = null;

    const linkForceForPulses = (pulses: SynapsePulse[]) => {
      const links: SimulationLinkDatum<SimNode>[] = pulses.map((p) => ({
        source: p.i,
        target: p.j,
      }));
      return forceLink<SimNode, SimulationLinkDatum<SimNode>>(links)
        .distance(56)
        .strength(0.22)
        .iterations(2);
    };

    const pickNode = (mx: number, my: number): number | null => {
      const nodes = nodesRef.current;
      const pad = 10;
      for (let i = nodes.length - 1; i >= 0; i--) {
        const d = nodes[i];
        if (d.x == null || d.y == null) continue;
        const edgeDist = Math.hypot(mx - d.x, my - d.y) - d.r;
        if (edgeDist <= pad) return i;
      }
      return null;
    };

    const endDrag = (e: PointerEvent) => {
      if (activePointerId !== e.pointerId || dragIndex === null) return;
      const idx = dragIndex;
      const pid = activePointerId;
      dragIndex = null;
      activePointerId = null;
      const d = nodesRef.current[idx];
      d.fx = null;
      d.fy = null;
      canvas.style.cursor = "";
      sim?.alphaTarget(0.055).alpha(0.25);
      try {
        canvas.releasePointerCapture(pid);
      } catch {
        /* ignore */
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || !sim) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const i = pickNode(mx, my);
      if (i === null) return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      activePointerId = e.pointerId;
      dragIndex = i;
      const d = nodesRef.current[i];
      if (d.x != null && d.y != null) {
        d.fx = d.x;
        d.fy = d.y;
      }
      canvas.style.cursor = "grabbing";
      sim.alphaTarget(0.35).alpha(0.45);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (dragIndex === null || e.pointerId !== activePointerId || !sim) return;
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const d = nodesRef.current[dragIndex];
      const r = d.r;
      d.fx = Math.max(r, Math.min(w - r, mx));
      d.fy = Math.max(r, Math.min(h - r, my));
    };

    const resize = (): { w: number; h: number } | null => {
      const cw = wrap.clientWidth;
      const ch = wrap.clientHeight;
      if (cw < 10 || ch < 10) return null;
      canvas.width = Math.floor(cw * dpr);
      canvas.height = Math.floor(ch * dpr);
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w: cw, h: ch };
    };

    const onWinMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      if (e.clientY < rect.top || e.clientY > rect.bottom) {
        mouseRef.current = null;
        return;
      }
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const applyMouse = () => {
      const m = mouseRef.current;
      if (!m) return;
      const nodes = nodesRef.current;
      for (const d of nodes) {
        if (d.x == null || d.y == null) continue;
        const dx = m.x - d.x;
        const dy = m.y - d.y;
        const dist = Math.hypot(dx, dy) + 0.01;
        if (dist < 200) {
          const pull = 0.055 * (1 - dist / 200);
          d.vx = (d.vx ?? 0) + (dx / dist) * pull;
          d.vy = (d.vy ?? 0) + (dy / dist) * pull;
        }
      }
    };

    const applyDrift = () => {
      if (mouseRef.current || dragIndex !== null) return;
      const nodes = nodesRef.current;
      const t = frameRef.current;
      const i = t % nodes.length;
      const j = (t + 17) % nodes.length;
      const a = nodes[i];
      const b = nodes[j];
      if (a?.x != null && a.y != null && b?.x != null && b.y != null) {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) + 0.01;
        const s = 0.018;
        a.vx = (a.vx ?? 0) - (dy / dist) * s;
        a.vy = (a.vy ?? 0) + (dx / dist) * s;
        b.vx = (b.vx ?? 0) + (dy / dist) * s;
        b.vy = (b.vy ?? 0) - (dx / dist) * s;
      }
    };

    const draw = () => {
      const nodes = nodesRef.current;
      const sp = scrollRef.current;
      const linkDist = 72 + sp * 100;
      const maxDist = linkDist + 40;

      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        if (a.x == null || a.y == null) continue;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          if (b.x == null || b.y == null) continue;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy);
          if (dist < maxDist && dist > 0.5) {
            const t = 1 - dist / maxDist;
            const alpha = 0.08 + t * 0.35 + sp * 0.12;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(79, 209, 197, ${alpha * t})`;
            ctx.lineWidth = 0.6 + sp * 0.4;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const pulse of synapsePulses) {
        const a = nodes[pulse.i];
        const b = nodes[pulse.j];
        if (a?.x == null || a.y == null || b?.x == null || b.y == null) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);
        if (dist >= maxDist || dist <= 0.5) continue;

        const px = a.x + dx * pulse.phase;
        const py = a.y + dy * pulse.phase;
        const pulseAlpha = 0.35 + sp * 0.4;

        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45, 212, 191, ${pulseAlpha * 0.28})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(px, py, 0.75, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(165, 243, 252, ${pulseAlpha * 0.75})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(px, py, 0.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.8 + sp * 0.15})`;
        ctx.fill();
      }

      for (const d of nodes) {
        if (d.x == null || d.y == null) continue;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r + 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45, 212, 191, ${0.06 + sp * 0.04})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(8, 18, 28, 0.92)";
        ctx.strokeStyle = `rgba(79, 209, 197, ${0.35 + sp * 0.25})`;
        ctx.lineWidth = 1;
        ctx.fill();
        ctx.stroke();
      }
    };

    const startLoop = () => {
      const loop = () => {
        if (cancelled || !sim) return;
        // Pause when tab is hidden or section is out of viewport
        if (document.hidden || !visibleRef.current) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        frameRef.current += 1;
        const isInteracting = mouseRef.current !== null || dragIndex !== null;
        applyMouse();
        // Only run drift + full physics tick when needed
        if (isInteracting || frameRef.current % 2 === 0) {
          applyDrift();
          for (const p of synapsePulses) {
            p.phase += p.speed;
            if (p.phase > 1) p.phase -= 1;
          }
          const a = sim.alpha();
          const target = sim.alphaTarget();
          if (a < target * 0.85) {
            sim.alpha(Math.min(0.35, a + target * 0.5));
          }
          sim.tick(isInteracting ? 2 : 1);
        }
        draw();
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    };

    const setupSimulation = () => {
      const dims = resize();
      if (!dims) return false;
      w = dims.w;
      h = dims.h;

      if (sim) {
        sim.stop();
        sim = null;
      }

      nodesRef.current = initNodes(w, h);
      synapsePulses = initSynapsePulses(nodesRef.current);

      sim = forceSimulation<SimNode>(nodesRef.current)
        .velocityDecay(0.28)
        .alphaMin(0.0008)
        .alphaDecay(0.022)
        .alphaTarget(0.055)
        .force(
          "charge",
          forceManyBody<SimNode>().strength((d) => -28 - d.r * 1.1),
        )
        .force("link", linkForceForPulses(synapsePulses))
        .force(
          "collide",
          forceCollide<SimNode>()
            .radius((d) => d.r + 1.4)
            .strength(0.9)
            .iterations(4),
        )
        .force("center", forceCenter(w / 2, h / 2))
        .force("x", forceX(w / 2).strength(0.08))
        .force("y", forceY(h / 2).strength(0.08));

      sim.alpha(0.95);
      sim.stop();
      return true;
    };

    const tryInit = () => {
      if (cancelled) return;
      if (!setupSimulation()) {
        retryRef.current = requestAnimationFrame(tryInit);
        return;
      }
      startLoop();
    };

    tryInit();

    const io = new IntersectionObserver(
      ([entry]) => { visibleRef.current = entry.isIntersecting; },
      { threshold: 0 }
    );
    io.observe(wrap);

    const ro = new ResizeObserver(() => {
      if (cancelled) return;
      const next = resize();
      if (!next || !sim) return;
      w = next.w;
      h = next.h;
      for (const d of nodesRef.current) {
        if (d.x != null) d.x = Math.min(w - d.r, Math.max(d.r, d.x));
        if (d.y != null) d.y = Math.min(h - d.r, Math.max(d.r, d.y));
      }
      sim.force("center", forceCenter(w / 2, h / 2));
      sim.force("x", forceX(w / 2).strength(0.08));
      sim.force("y", forceY(h / 2).strength(0.08));
      sim.force("link", linkForceForPulses(synapsePulses));
      sim.alpha(0.5);
      sim.stop();
      synapsePulses = initSynapsePulses(nodesRef.current);
    });
    ro.observe(wrap);

    window.addEventListener("mousemove", onWinMove, { passive: true });

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", endDrag);
    canvas.addEventListener("pointercancel", endDrag);

    return () => {
      cancelled = true;
      io.disconnect();
      if (activePointerId !== null && dragIndex !== null) {
        const d = nodesRef.current[dragIndex];
        d.fx = null;
        d.fy = null;
        try {
          canvas.releasePointerCapture(activePointerId);
        } catch {
          /* ignore */
        }
        dragIndex = null;
        activePointerId = null;
        canvas.style.cursor = "";
      }
      window.removeEventListener("mousemove", onWinMove);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", endDrag);
      canvas.removeEventListener("pointercancel", endDrag);
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
      cancelAnimationFrame(retryRef.current);
      sim?.stop();
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={wrapRef}
      className="pointer-events-auto absolute inset-0 h-full min-h-[280px] w-full"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block h-full w-full cursor-grab touch-none select-none active:cursor-grabbing"
        aria-hidden
      />
    </div>
  );
}
